import {
  Box,
  Button,
  Text,
  Container,
  VStack,
  HStack,
  Icon,
  Heading,
  useColorModeValue,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Badge,
  Avatar,
  Image,
  useToast,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaArrowLeft, FaCalendarAlt, FaRoute, FaUsers, FaIndustry, FaBuilding } from "react-icons/fa";
import { MdBusiness, MdSchedule } from "react-icons/md";
import SSLCertificateHelper from "../components/SSLCertificateHelper";

function EscolherAcao() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const sslHelperDisclosure = useDisclosure();
  
  const [sslError, setSslError] = useState(null);
  
  // Cores responsivas
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedTextColor = useColorModeValue("gray.600", "gray.400");

  // Verificar se o motorista foi passado via state
  const motorista = location.state?.motorista;
  const periodoInicial = location.state?.periodoInicial || "3dias";

  useEffect(() => {
    if (!motorista) {
      toast({
        title: "Motorista não encontrado",
        description: "Por favor, selecione um motorista primeiro",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      navigate("/", { replace: true });
    }
  }, [motorista, navigate, toast]);

  const getEmpresaConfig = (empresa) => {
    switch (empresa) {
      case "IMPACTO":
        return {
          color: "blue",
          borderColor: "blue.500",
          icon: FaIndustry,
          bgColor: "blue.50",
          logo: "/impacto_transportes_ltda_logo.jpg",
          gradient: "linear-gradient(135deg, #EBF8FF 0%, #BEE3F8 100%)"
        };
      case "CENZE":
        return {
          color: "purple",
          borderColor: "purple.500",
          icon: MdBusiness,
          bgColor: "purple.50",
          logo: "/trr_cenze_comrcio_de_combustveis_e_derivados_logo.jpg",
          gradient: "linear-gradient(135deg, #FAF5FF 0%, #E9D8FD 100%)"
        };
      case "SEM EMPRESA":
      default:
        return {
          color: "gray",
          borderColor: "gray.500",
          icon: FaBuilding,
          bgColor: "gray.50",
          logo: null,
          gradient: "linear-gradient(135deg, #F7FAFC 0%, #EDF2F7 100%)"
        };
    }
  };

  const handleVerApontamentos = () => {
    navigate(`/${motorista.codigo}/apontamentos`, {
      state: { motorista, periodoInicial },
    });
  };

  const handleVerEventosAtuais = () => {
    navigate(`/${motorista.codigo}/eventos-atuais`, {
      state: { motorista, periodoInicial: "hoje" },
    });
  };

  const handleVoltar = () => {
    navigate("/", { replace: true });
  };

  if (!motorista) {
    return null;
  }

  const empresaConfig = getEmpresaConfig(motorista.empresa);

  return (
    <Box bg={bgColor} minH="100vh" py={6}>
      <Container maxW="6xl" px={4}>
        {/* Header com navegação */}
        <Flex justify="space-between" align="center" mb={8}>
          <Button
            leftIcon={<FaArrowLeft />}
            colorScheme="gray"
            variant="ghost"
            size="lg"
            onClick={handleVoltar}
            _hover={{ bg: "gray.100", transform: "translateX(-2px)" }}
            borderRadius="xl"
            px={6}
            transition="all 0.2s"
            fontWeight="medium"
          >
            Voltar à Seleção
          </Button>
        </Flex>

  

        {/* Card do motorista selecionado */}
        <Card 
          bg={cardBg} 
          border="2px solid" 
          borderColor={empresaConfig.borderColor}
          borderRadius="2xl" 
          boxShadow="xl"
          mb={8}
          overflow="hidden"
        >
          <CardHeader 
            bg={empresaConfig.gradient}
            borderBottom="1px solid" 
            borderColor={borderColor}
          >
            <Flex align="center" gap={4}>
              <Box
                w="60px"
                h="60px"
                borderRadius="full"
                border="2px solid"
                borderColor={empresaConfig.borderColor}
                bg="white"
                overflow="hidden"
                display="flex"
                alignItems="center"
                justifyContent="center"
                boxShadow="lg"
                flexShrink={0}
              >
                {empresaConfig.logo ? (
                  <Image
                    src={empresaConfig.logo}
                    alt={`Logo ${motorista.empresa}`}
                    w="100%"
                    h="100%"
                    objectFit="contain"
                    borderRadius="full"
                  />
                ) : (
                  <Icon 
                    as={empresaConfig.icon} 
                    color={empresaConfig.borderColor} 
                    boxSize={8} 
                  />
                )}
              </Box>
              <Box flex={1}>
                <Heading size="lg" color={textColor} mb={2}>
                  {motorista.nome}
                </Heading>
                <HStack spacing={4} flexWrap="wrap">
                  <Badge
                    colorScheme={empresaConfig.color}
                    variant="solid"
                    fontSize="sm"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontWeight="medium"
                  >
                    {motorista.empresa}
                  </Badge>
                  <Badge
                    colorScheme="gray"
                    variant="outline"
                    fontSize="sm"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontWeight="medium"
                  >
                    Código: {motorista.codigo}
                  </Badge>
                </HStack>
              </Box>
            </Flex>
          </CardHeader>
        </Card>

        {/* Opções de ação */}
        <VStack spacing={6} maxW="4xl" mx="auto">
          {/* Opção 1: Ver Apontamentos */}
          <Card 
            bg={cardBg} 
            border="1px solid" 
            borderColor={borderColor} 
            borderRadius="2xl" 
            boxShadow="lg"
            w="100%"
            _hover={{ 
              boxShadow: "2xl", 
              transform: "translateY(-4px)",
              borderColor: "red.500"
            }}
            transition="all 0.3s ease-in-out"
            cursor="pointer"
            onClick={handleVerApontamentos}
          >
            <CardBody p={8}>
              <Flex align="center" gap={6}>
                <Box
                  bg="linear-gradient(135deg, #E31E2C 0%, #C51B26 100%)"
                  w="80px"
                  h="80px"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="xl"
                  flexShrink={0}
                >
                  <Icon as={FaCalendarAlt} boxSize={10} color="white" />
                </Box>
                <Box flex={1}>
                  <Heading size="lg" color={textColor} mb={3}>
                    Ver Apontamentos
                  </Heading>
                  <Text fontSize="lg" color={mutedTextColor} mb={4} lineHeight="tall">
                    Acesse o histórico completo de apontamentos deste motorista, incluindo 
                    horários de entrada, saída, pausas e todas as atividades registradas.
                  </Text>
                  <HStack spacing={4} flexWrap="wrap">
                    <Badge colorScheme="blue" variant="outline" px={3} py={1}>
                      Histórico Completo
                    </Badge>
                    <Badge colorScheme="green" variant="outline" px={3} py={1}>
                      Relatórios Detalhados
                    </Badge>
                    <Badge colorScheme="purple" variant="outline" px={3} py={1}>
                      Análise de Tempo
                    </Badge>
                  </HStack>
                </Box>
                <Icon 
                  as={FaArrowLeft} 
                  boxSize={6} 
                  color={mutedTextColor}
                  transform="rotate(180deg)"
                />
              </Flex>
            </CardBody>
          </Card>

          {/* Opção 2: Ver Eventos Atuais */}
          <Card 
            bg={cardBg} 
            border="1px solid" 
            borderColor={borderColor} 
            borderRadius="2xl" 
            boxShadow="lg"
            w="100%"
            _hover={{ 
              boxShadow: "2xl", 
              transform: "translateY(-4px)",
              borderColor: "blue.500"
            }}
            transition="all 0.3s ease-in-out"
            cursor="pointer"
            onClick={handleVerEventosAtuais}
          >
            <CardBody p={8}>
              <Flex align="center" gap={6}>
                <Box
                  bg="linear-gradient(135deg, #3182CE 0%, #2C5AA0 100%)"
                  w="80px"
                  h="80px"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="xl"
                  flexShrink={0}
                >
                  <Icon as={FaRoute} boxSize={10} color="white" />
                </Box>
                <Box flex={1}>
                  <Heading size="lg" color={textColor} mb={3}>
                    Ver Eventos Atuais
                  </Heading>
                  <Text fontSize="lg" color={mutedTextColor} mb={4} lineHeight="tall">
                    Visualize os eventos em tempo real deste motorista, incluindo direção, 
                    pausas, refeições, cargas e descargas com localização e horários.
                  </Text>
                  <HStack spacing={4} flexWrap="wrap">
                    <Badge colorScheme="blue" variant="outline" px={3} py={1}>
                      Tempo Real
                    </Badge>
                    <Badge colorScheme="green" variant="outline" px={3} py={1}>
                      Localização GPS
                    </Badge>
                    <Badge colorScheme="orange" variant="outline" px={3} py={1}>
                      Status Atual
                    </Badge>
                  </HStack>
                </Box>
                <Icon 
                  as={FaArrowLeft} 
                  boxSize={6} 
                  color={mutedTextColor}
                  transform="rotate(180deg)"
                />
              </Flex>
            </CardBody>
          </Card>
        </VStack>

        {/* Informação adicional */}
        <Box
          mt={12}
          p={6}
          bg="blue.50"
          borderRadius="xl"
          border="1px solid"
          borderColor="blue.200"
          textAlign="center"
        >
          <Text fontSize="md" color="blue.700" fontWeight="medium">
            💡 <strong>Dica:</strong> Os apontamentos mostram o histórico completo, enquanto os eventos atuais 
            mostram a situação em tempo real com localização GPS.
          </Text>
        </Box>
      </Container>

      {/* SSL Certificate Helper Modal */}
      <SSLCertificateHelper
        isOpen={sslHelperDisclosure.isOpen}
        onClose={sslHelperDisclosure.onClose}
        error={sslError}
        baseUrl={sslError?.baseUrl}
      />
    </Box>
  );
}

export default EscolherAcao;
